import { useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { DateTime } from "luxon";
import { AlertCircle, Calendar, CloudRain } from "lucide-react";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { Box, BoxHeader, BoxContent } from "./Box";
import { event, type EventCountsByPeriod, type EventStatisticsGranularity } from "@/endpoints/dryp/event";

type EventBucket = {
	label: string;
	shortLabel: string;
	key: string;
	overflowCount: number;
	rainCount: number;
	blockageCount: number;
};

const Legend = () => (
	<div className="flex items-center gap-3 text-[10px]">
		<div className="flex items-center gap-1">
			<div className="w-2 h-2 rounded-sm bg-red-500" />
			<span className="text-muted-foreground">Overflow</span>
		</div>
		<div className="flex items-center gap-1">
			<div className="w-2 h-2 rounded-sm bg-amber-500" />
			<span className="text-muted-foreground">Blockage</span>
		</div>
		<div className="flex items-center gap-1">
			<div className="w-2 h-2 rounded-sm bg-sky-500" />
			<span className="text-muted-foreground">Rain</span>
		</div>
	</div>
);

const StatItem = ({ label, value, color }: { label: string; value: string | number; color?: string }) => (
	<div className="flex flex-col items-center">
		<span className={`text-sm font-medium ${color || "text-muted-foreground"}`}>{value}</span>
		<span className="text-[10px] text-muted-foreground">{label}</span>
	</div>
);

const EventBar = ({ event, maxTotal }: { event: EventBucket; maxTotal: number }) => {
	const total = event.overflowCount + event.blockageCount + event.rainCount;
	const overflowHeight = maxTotal > 0 ? (event.overflowCount / maxTotal) * 100 : 0;
	const blockageHeight = maxTotal > 0 ? (event.blockageCount / maxTotal) * 100 : 0;
	const rainHeight = maxTotal > 0 ? (event.rainCount / maxTotal) * 100 : 0;

	const segments = [
		{ count: event.rainCount, height: rainHeight, className: "bg-sky-500" },
		{ count: event.blockageCount, height: blockageHeight, className: "bg-amber-500" },
		{ count: event.overflowCount, height: overflowHeight, className: "bg-red-500" },
	];
	const firstVisibleIndex = segments.findIndex(s => s.count > 0);

	return (
		<TooltipProvider>
			<Tooltip>
				<TooltipTrigger asChild>
					<div className="flex flex-col items-center gap-1 flex-1 min-w-0">
						{event.rainCount > 0 ? (
							<CloudRain className="w-3.5 h-3.5 text-sky-500" />
						) : (
							<div className="w-3.5 h-3.5" />
						)}
						<div className="w-full h-24 flex flex-col justify-end items-center">
							{total > 0 ? (
								<>
									{segments.map((segment, index) =>
										segment.count > 0 ? (
											<div
												key={index}
												className={`w-full max-w-6 ${segment.className}`}
												style={{
													height: `${segment.height}%`,
													borderRadius:
														index === firstVisibleIndex ? "0.125rem 0.125rem 0 0" : "0",
												}}
											/>
										) : null,
									)}
								</>
							) : (
								<div className="w-full max-w-6 h-1 rounded-sm bg-muted" />
							)}
						</div>
						<span className="text-[10px] text-muted-foreground">{event.shortLabel}</span>
					</div>
				</TooltipTrigger>
				<TooltipContent className="bg-popover text-popover-foreground border border-border">
					<div className="text-xs space-y-1">
						<p className="font-medium">{event.label}</p>
						<p>Overflow: {event.overflowCount}</p>
						<p>Blockage: {event.blockageCount}</p>
						<p>Rain: {event.rainCount}</p>
					</div>
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	);
};

const EventsLoading = () => (
	<Box>
		<BoxHeader icon={<Calendar className="w-4 h-4" />} title="Events" />
		<BoxContent>
			<div className="space-y-3">
				<Skeleton className="h-4 w-32" />
				<Skeleton className="h-24 w-full" />
				<div className="flex justify-between">
					{Array.from({ length: 7 }).map((_, i) => (
						<Skeleton key={i} className="h-4 w-8" />
					))}
				</div>
			</div>
		</BoxContent>
	</Box>
);

type BucketSpan = { start: DateTime; end: DateTime };

const buildSpans = (from: string, to: string): BucketSpan[] => {
	const start = DateTime.fromISO(from);
	const end = DateTime.fromISO(to);
	const totalDays = end.diff(start, "days").days;

	const spans: BucketSpan[] = [];

	if (totalDays <= 2) {
		// 24h → 6 chunks of 4 hours
		let current = start;
		while (current < end) {
			const next = current.plus({ hours: 4 });
			spans.push({ start: current, end: next < end ? next : end });
			current = next;
		}
	} else if (totalDays <= 7) {
		// 7d → one per day
		let current = start.startOf("day");
		while (current < end) {
			const next = current.plus({ days: 1 });
			spans.push({ start: current, end: next });
			current = next;
		}
	} else if (totalDays <= 14) {
		// 14d → 7 x 2-day pairs
		let current = start.startOf("day");
		while (current < end) {
			const next = current.plus({ days: 2 });
			spans.push({ start: current, end: next < end ? next : end });
			current = next;
		}
	} else if (totalDays <= 45) {
		// 30d → weekly buckets
		let current = start.startOf("day");
		while (current < end) {
			const next = current.plus({ weeks: 1 });
			spans.push({ start: current, end: next < end ? next : end });
			current = next;
		}
	} else {
		// 90d / 1y+ → monthly buckets
		let current = start.startOf("month");
		while (current < end) {
			const next = current.plus({ months: 1 });
			spans.push({ start: current, end: next < end ? next : end });
			current = next;
		}
	}

	return spans;
};

const formatBucket = (span: BucketSpan, totalDays: number, index: number): { label: string; shortLabel: string } => {
	if (totalDays <= 2) {
		return {
			label: `${span.start.toFormat("HH:mm")} – ${span.end.toFormat("HH:mm")}`,
			shortLabel: `${span.start.toFormat("HH")}-${span.end.toFormat("HH")}`,
		};
	} else if (totalDays <= 7) {
		return { label: span.start.toFormat("cccc, MMM d"), shortLabel: span.start.toFormat("ccc") };
	} else if (totalDays <= 14) {
		return {
			label: `${span.start.toFormat("ccc d")} – ${span.end.minus({ days: 1 }).toFormat("ccc d")}`,
			shortLabel: `${span.start.toFormat("ccc")}-${span.end.minus({ days: 1 }).toFormat("ccc")}`,
		};
	} else if (totalDays <= 45) {
		return {
			label: `Week of ${span.start.toFormat("MMM d")}`,
			shortLabel: `W${index + 1}`,
		};
	} else {
		return { label: span.start.toFormat("MMMM yyyy"), shortLabel: span.start.toFormat("MMM") };
	}
};

const deriveGranularity = (from: string, to: string): EventStatisticsGranularity => {
	const totalDays = DateTime.fromISO(to).diff(DateTime.fromISO(from), "days").days;
	if (totalDays <= 2) return "Hour";
	if (totalDays <= 45) return "Day";
	return "Month";
};

const buildBuckets = (byPeriod: EventCountsByPeriod[], from: string, to: string): EventBucket[] => {
	if (!from || !to) return [];
	const totalDays = DateTime.fromISO(to).diff(DateTime.fromISO(from), "days").days;
	const spans = buildSpans(from, to);

	return spans.map((span, index) => {
		const { label, shortLabel } = formatBucket(span, totalDays, index);
		const spanStartMs = span.start.toMillis();
		const spanEndMs = span.end.toMillis();

		// Sum counts for all byPeriod entries whose periodStart falls within this span.
		// byPeriod only contains periods with at least one event, so spans with no
		// matching entries naturally produce zero counts (height-zero bars).
		const counts = byPeriod
			.filter(entry => {
				const entryMs = DateTime.fromISO(entry.periodStart).toMillis();
				return entryMs >= spanStartMs && entryMs < spanEndMs;
			})
			.reduce(
				(acc, entry) => ({
					overflowCount: acc.overflowCount + entry.overflowCount,
					rainCount: acc.rainCount + entry.rainCount,
					blockageCount: acc.blockageCount + entry.blockageCount,
				}),
				{ overflowCount: 0, rainCount: 0, blockageCount: 0 },
			);

		return {
			label,
			shortLabel,
			key: span.start.toISO()!,
			...counts,
		};
	});
};

export const StructureEvents = observer(
	({ structureId, from, to }: { structureId: Dryp.StructureId; from: string; to: string }) => {
		const statisticsApi = useAsyncStateStore(event.getStatistics, { debounce: 300 });

		useEffect(() => {
			if (!from || !to) return;
			statisticsApi.fetch(from, to, Number(structureId), deriveGranularity(from, to));
		}, [structureId, from, to]);

		const byPeriod = statisticsApi.data?.byPeriod ?? [];
		const buckets = useMemo(() => buildBuckets(byPeriod, from, to), [byPeriod, from, to]);

		if (!statisticsApi.loaded) {
			return <EventsLoading />;
		}

		if (statisticsApi.error) {
			return (
				<Box>
					<BoxHeader icon={<Calendar className="w-4 h-4" />} title="Events" />
					<BoxContent>
						<div className="flex items-center gap-2 text-red-500">
							<AlertCircle className="w-4 h-4 shrink-0" />
							<p className="text-sm">Failed to load events</p>
						</div>
					</BoxContent>
				</Box>
			);
		}

		const maxTotal = Math.max(
			...buckets.map(bucket => bucket.overflowCount + bucket.blockageCount + bucket.rainCount),
			1,
		);
		const totalOverflow = statisticsApi.data?.overflowCount ?? 0;
		const totalBlockage = statisticsApi.data?.blockageCount ?? 0;
		const totalRain = statisticsApi.data?.rainCount ?? 0;
		const rainyDays = statisticsApi.data?.rainInDays ?? 0;

		return (
			<Box>
				<BoxHeader icon={<Calendar className="w-4 h-4" />} title="Events" action={<Legend />} />
				<BoxContent className="space-y-3">
					<div className="flex items-end gap-1">
						{buckets.map(bucket => (
							<EventBar key={bucket.key} event={bucket} maxTotal={maxTotal} />
						))}
					</div>
					<div className="flex items-center justify-between pt-1">
						<StatItem label="overflows" value={totalOverflow} color="text-red-500" />
						<StatItem label="blockages" value={totalBlockage} color="text-amber-500" />
						<StatItem label="rain events" value={totalRain} color="text-sky-500" />
						<StatItem label="rainy days" value={rainyDays} />
					</div>
				</BoxContent>
			</Box>
		);
	},
);
